import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { Paragraph } from '../models/Paragraph';
import { TextElement } from '../models/TranscriptElement';
import { v4 as uuidv4 } from 'uuid';

/**
 * Command state for SplitParagraphCommand
 * Captures the state needed for undo operations
 */
interface SplitParagraphState {
  segmentId: string;
  originalParagraphId: string;
  newParagraphId: string;
  originalElements: any[];
  originalStartTime: number;
  originalEndTime: number;
  splitPosition: number;
}

/**
 * Command to split a paragraph at a specified element
 * 
 * This command takes a paragraph and splits it into two paragraphs at the
 * specified element. It handles element distribution and timing adjustments.
 */
export class SplitParagraphCommand extends AbstractTranscriptEditorCommand {
  /** ID of the segment containing the paragraph */
  private segmentId: string;
  
  /** ID of the paragraph to split */
  private paragraphId: string;
  
  /** ID of the element to split at */
  private elementId: string;
  
  /** User ID for operation tracking */
  private userId: string;
  
  /** New paragraph ID that will be created */
  private newParagraphId: string;
  
  /** State captured for undo operations */
  private state: SplitParagraphState | null = null;
  
  /**
   * Creates a new SplitParagraphCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segmentId ID of the segment containing the paragraph
   * @param paragraphId ID of the paragraph to split
   * @param elementId ID of the element to split at
   * @param userId ID of the user making the change
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    paragraphId: string,
    elementId: string,
    userId: string
  ) {
    super(`Split paragraph at element ${elementId}`, editor);
    
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.elementId = elementId;
    this.userId = userId;
    this.newParagraphId = uuidv4();
    
    // Store metadata for debugging and tracking
    this.setMetadata('segmentId', segmentId);
    this.setMetadata('paragraphId', paragraphId);
    this.setMetadata('elementId', elementId);
    this.setMetadata('userId', userId);
    this.setMetadata('newParagraphId', this.newParagraphId);
  }
  
  /**
   * Execute the command
   * 
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segment
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      // Get the paragraph
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        return false;
      }
      
      // Find index of the element to split at
      const elementIndex = paragraph.elements.findIndex(el => el.id === this.elementId);
      if (elementIndex === -1) {
        return false;
      }
      
      // Split the elements at the element index
      // Elements after the split point go to the new paragraph
      const elementsForNewParagraph = paragraph.elements.slice(elementIndex + 1);
      const elementsForOldParagraph = paragraph.elements.slice(0, elementIndex + 1);
      
      // Make sure we have valid elements on both sides
      if (elementsForNewParagraph.length === 0 || elementsForOldParagraph.length === 0) {
        return false;
      }
      
      // Filter down to text elements for timing calculations
      const newTextElements = elementsForNewParagraph.filter(
        el => el.type === 'text'
      ) as TextElement[];
      
      const oldTextElements = elementsForOldParagraph.filter(
        el => el.type === 'text'
      ) as TextElement[];
      
      // Make sure we have at least one text element on each side
      if (newTextElements.length === 0 || oldTextElements.length === 0) {
        return false;
      }
      
      // Calculate timing for the new paragraphs
      const newStartTimes = newTextElements
        .map(el => el.startTime)
        .filter(time => time !== undefined) as number[];
      
      const newEndTimes = newTextElements
        .map(el => el.endTime)
        .filter(time => time !== undefined) as number[];
      
      const oldStartTimes = oldTextElements
        .map(el => el.startTime)
        .filter(time => time !== undefined) as number[];
      
      const oldEndTimes = oldTextElements
        .map(el => el.endTime)
        .filter(time => time !== undefined) as number[];
      
      // Determine new timing
      const newStartTime = newStartTimes.length > 0 ? Math.min(...newStartTimes) : paragraph.startTime;
      const newEndTime = newEndTimes.length > 0 ? Math.max(...newEndTimes) : paragraph.endTime;
      const oldStartTime = oldStartTimes.length > 0 ? Math.min(...oldStartTimes) : paragraph.startTime;
      const oldEndTime = oldEndTimes.length > 0 ? Math.max(...oldEndTimes) : paragraph.endTime;
      
      // Store original paragraph state for undo
      const originalElements = [...paragraph.elements];
      const originalStartTime = paragraph.startTime;
      const originalEndTime = paragraph.endTime;
      
      // Create deep copies of the elements to avoid reference issues
      const copiedNewElements = elementsForNewParagraph.map(el => ({ ...el }));
      const copiedOldElements = elementsForOldParagraph.map(el => ({ ...el }));
      
      // Create new paragraph
      const ParagraphClass = this.editor.getParagraphClass();
      const newParagraph = new ParagraphClass(
        this.newParagraphId,
        newStartTime,
        newEndTime,
        copiedNewElements,
        paragraph.segmentId
      );
      
      // Update the original paragraph with first half of elements
      paragraph.elements = copiedOldElements;
      paragraph.startTime = oldStartTime;
      paragraph.endTime = oldEndTime;
      
      // Rebuild element map in the paragraph (important!)
      paragraph.rebuildElementMap();
      
      // Add the new paragraph to the segment
      segment.addParagraph(newParagraph);
      
      // Sort paragraphs by time for proper ordering
      this.editor.sortParagraphsByTime(segment);
      
      // Record the operation
      this.editor.recordOperation({
        type: 'paragraph_split',
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        data: {
          newParagraphId: this.newParagraphId,
          elementId: this.elementId,
          elementIndex,
          oldTiming: { start: oldStartTime, end: oldEndTime },
          newTiming: { start: newStartTime, end: newEndTime }
        },
        userId: this.userId,
        timestamp: Date.now()
      });
      
      // Notify observers about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_SPLIT, {
        segment,
        originalParagraph: paragraph,
        newParagraph
      });
      
      // Store state for undo
      this.state = {
        segmentId: this.segmentId,
        originalParagraphId: this.paragraphId,
        newParagraphId: this.newParagraphId,
        originalElements,
        originalStartTime,
        originalEndTime,
        splitPosition: elementIndex
      };
      
      return true;
    } catch (error) {
      console.error('Error executing SplitParagraphCommand:', error);
      
      return false;
    }
  }
  
  /**
   * Undo the command
   * 
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    
    try {
      if (!this.state) {
        console.error('Cannot undo SplitParagraphCommand: state is null');
        return false;
      }
      
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segment
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      // Get the paragraphs
      const originalParagraph = segment.getParagraph(this.paragraphId);
      const newParagraph = segment.getParagraph(this.state.newParagraphId);
      
      if (!originalParagraph) {
        return false;
      }
      
      // Restore the original elements to the first paragraph
      originalParagraph.elements = [...this.state.originalElements];
      originalParagraph.startTime = this.state.originalStartTime;
      originalParagraph.endTime = this.state.originalEndTime;
      
      // Rebuild element map in paragraph
      originalParagraph.rebuildElementMap();
      
      // Remove the second paragraph
      if (newParagraph) {
        segment.removeParagraph(this.state.newParagraphId);
      }
      
      // Notify observers about the change
      this.editor.notifyObservers(TranscriptEditorChangeType.PARAGRAPH_MERGED, {
        segment,
        paragraph: originalParagraph,
        mergedParagraphId: this.state.newParagraphId
      });
      
      return true;
    } catch (error) {
      console.error('Error undoing SplitParagraphCommand:', error);
      
      return false;
    }
  }
}